import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { type FormEvent, useState } from "react";
import { ciRoleCan } from "../../shared/permissions";
import { Badge } from "../components/Badge";
import { Button } from "../components/Button";
import { EmptyState, ErrorBanner, Loading } from "../components/Feedback";
import { Modal } from "../components/Modal";
import {
  cleanupExpired,
  createMock,
  fetchMocks,
  type MockListFilters,
  mockKeys,
  MOCK_PAGE_SIZE,
} from "../features/mocks/mock-queries";
import { type CiSession } from "../lib/auth-client";
import {
  formatDateTime,
  formatRelative,
  isExpired,
  pluralize,
} from "../lib/format";
import { Link, useRouter } from "../lib/router";

type MocksPageProps = {
  /** Signed-in session */
  session: CiSession;
};

type NewMockModalProps = {
  /** Closes the dialog */
  onClose: () => void;
};

const NewMockModal = ({ onClose }: NewMockModalProps) => {
  const { navigate } = useRouter();
  const queryClient = useQueryClient();
  const create = useMutation({
    mutationFn: createMock,
    onSuccess: (mock) => {
      void queryClient.invalidateQueries({ queryKey: mockKeys.all });
      navigate(`/mocks/${mock.uuid}`);
    },
  });
  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    const days = String(form.get("expiry"));
    create.mutate({
      label: String(form.get("label")).trim() || null,
      ...(days === "never"
        ? { expiresAt: null }
        : { expiresInDays: Number(days) }),
    });
  };
  return (
    <Modal title="New API mock" onClose={onClose}>
      <form className="space-y-4" onSubmit={submit}>
        <ErrorBanner error={create.error} />
        <div>
          <label className="label" htmlFor="label">
            Label
          </label>
          <input
            id="label"
            name="label"
            className="input"
            placeholder="e.g. BillHighway member sync"
            autoFocus
            maxLength={200}
          />
        </div>
        <div>
          <label className="label" htmlFor="expiry">
            Expires
          </label>
          <select id="expiry" name="expiry" className="input" defaultValue="30">
            <option value="1">in 1 day</option>
            <option value="7">in 7 days</option>
            <option value="30">in 30 days</option>
            <option value="365">in 1 year</option>
            <option value="never">never</option>
          </select>
        </div>
        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" disabled={create.isLoading}>
            Create mock
          </Button>
        </div>
      </form>
    </Modal>
  );
};

/** List of API mocks with search and filters. */
export const MocksPage = ({ session }: MocksPageProps) => {
  const queryClient = useQueryClient();
  const [filters, setFilters] = useState<MockListFilters>({
    search: "",
    mine: false,
    active: true,
    offset: 0,
  });
  const [creating, setCreating] = useState(false);
  const mocks = useQuery({
    queryKey: mockKeys.list(filters),
    queryFn: () => fetchMocks(filters),
    keepPreviousData: true,
  });
  const cleanup = useMutation({
    mutationFn: cleanupExpired,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: mockKeys.all }),
  });
  const canWrite = ciRoleCan({
    role: session.user.role,
    permissions: { mock: ["write"] },
  });
  const canDelete = ciRoleCan({
    role: session.user.role,
    permissions: { mock: ["delete"] },
  });
  const total = mocks.data?.total ?? 0;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold">API mocks</h1>
          <p className="text-sm text-slate-500">
            Canned responses for third-party APIs and webhook receivers. Every
            request a mock gets is recorded.
          </p>
        </div>
        <div className="flex gap-2">
          {canDelete ? (
            <Button
              variant="secondary"
              onClick={() => cleanup.mutate()}
              disabled={cleanup.isLoading}
            >
              {cleanup.data
                ? `Removed ${cleanup.data.deleted} expired`
                : "Remove expired"}
            </Button>
          ) : null}
          {canWrite ? (
            <Button onClick={() => setCreating(true)}>New mock</Button>
          ) : null}
        </div>
      </div>

      <div className="card flex flex-wrap items-center gap-4 px-4 py-3">
        <input
          className="input max-w-xs"
          type="search"
          placeholder="Search label, token, spec or test"
          value={filters.search}
          onChange={(event) =>
            setFilters({ ...filters, search: event.target.value, offset: 0 })
          }
        />
        <label className="flex items-center gap-2 text-sm text-slate-600">
          <input
            type="checkbox"
            checked={filters.mine}
            onChange={(event) =>
              setFilters({ ...filters, mine: event.target.checked, offset: 0 })
            }
          />
          Created by me
        </label>
        <label className="flex items-center gap-2 text-sm text-slate-600">
          <input
            type="checkbox"
            checked={filters.active}
            onChange={(event) =>
              setFilters({
                ...filters,
                active: event.target.checked,
                offset: 0,
              })
            }
          />
          Hide expired
        </label>
        <span className="ml-auto text-sm text-slate-500">
          {pluralize(total, "mock")}
        </span>
      </div>

      <ErrorBanner error={mocks.error ?? cleanup.error} />
      <div className="card overflow-x-auto">
        {mocks.isLoading ? (
          <Loading />
        ) : !mocks.data?.items.length ? (
          <EmptyState title="No mocks match">
            {canWrite
              ? "Create one, or let a Cypress test create one with cy.task('setupWebhookMockTask')."
              : null}
          </EmptyState>
        ) : (
          <table className="min-w-full divide-y divide-slate-200">
            <thead className="bg-slate-50">
              <tr>
                <th className="table-head">Mock</th>
                <th className="table-head">Source</th>
                <th className="table-head text-right">Routes</th>
                <th className="table-head text-right">Requests</th>
                <th className="table-head">Last request</th>
                <th className="table-head">Expires</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {mocks.data.items.map((mock) => (
                <tr key={mock.uuid} className="hover:bg-slate-50">
                  <td className="table-cell">
                    <Link
                      to={`/mocks/${mock.uuid}`}
                      className="font-medium text-sky-700 hover:underline"
                    >
                      {mock.label || "Untitled mock"}
                    </Link>
                    <div className="font-mono text-xs text-slate-400">
                      {mock.uuid}
                    </div>
                  </td>
                  <td className="table-cell text-slate-600">
                    {mock.createdBy ? (
                      mock.createdBy.name || mock.createdBy.email
                    ) : (
                      <span title={mock.ci.testName ?? undefined}>
                        <Badge tone="sky">CI</Badge>{" "}
                        {mock.ci.testSuite ??
                          `run ${mock.ci.githubRunnerId ?? "?"}`}
                      </span>
                    )}
                  </td>
                  <td className="table-cell text-right tabular-nums">
                    {mock.routeCount}
                  </td>
                  <td className="table-cell text-right tabular-nums">
                    {mock.requestCount}
                  </td>
                  <td
                    className="table-cell text-slate-600"
                    title={formatDateTime(mock.lastRequestAt)}
                  >
                    {formatRelative(mock.lastRequestAt)}
                  </td>
                  <td
                    className="table-cell"
                    title={formatDateTime(mock.expiresAt)}
                  >
                    {isExpired(mock.expiresAt) ? (
                      <Badge tone="rose">expired</Badge>
                    ) : mock.expiresAt ? (
                      formatRelative(mock.expiresAt)
                    ) : (
                      "never"
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      {total > MOCK_PAGE_SIZE ? (
        <div className="flex items-center justify-end gap-2 text-sm">
          <Button
            variant="secondary"
            size="sm"
            disabled={filters.offset === 0}
            onClick={() =>
              setFilters({
                ...filters,
                offset: Math.max(0, filters.offset - MOCK_PAGE_SIZE),
              })
            }
          >
            Previous
          </Button>
          <span className="text-slate-500">
            {filters.offset + 1}–
            {Math.min(total, filters.offset + MOCK_PAGE_SIZE)} of {total}
          </span>
          <Button
            variant="secondary"
            size="sm"
            disabled={filters.offset + MOCK_PAGE_SIZE >= total}
            onClick={() =>
              setFilters({
                ...filters,
                offset: filters.offset + MOCK_PAGE_SIZE,
              })
            }
          >
            Next
          </Button>
        </div>
      ) : null}
      {creating ? <NewMockModal onClose={() => setCreating(false)} /> : null}
    </div>
  );
};
